import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import API from '../../API/Main/API';
import {
	AdpRateMappingCreateProperties,
	AdpRateMappingProperties,
} from '../../API/ClientAPIHelpers/adpRateMapping';
import { ErrorProperties } from '../../API/identityAPIProperties';
import { resetState } from '../../resetState';

interface AdpRateMappingState {
	currentRequestId?: string;
	error: ErrorProperties;
	loadFailed: boolean;
	loading: boolean;
	mappings: AdpRateMappingProperties[];
	serviceCodeView: boolean;
}

const initialState: AdpRateMappingState = {
	serviceCodeView: false,
	mappings: [],
	loadFailed: false,
	loading: false,
	error: { status: 0, title: '' },
};

export const getAdpRateMappings = createAsyncThunk<
	AdpRateMappingProperties[],
	boolean | undefined,
	{ state: { adpRateMapping: AdpRateMappingState } }
>(
	'adpRateMapping/getAll',
	async (serviceCode, thunkAPI) => {
		const currentView = serviceCode ?? thunkAPI.getState().adpRateMapping.serviceCodeView;
		const response = currentView
			? await API.ClientAPI.AdpRateMapping.getServiceMappings()
			: await API.ClientAPI.AdpRateMapping.getAll();
		if (response.error) return thunkAPI.rejectWithValue(response.error);

		return response as AdpRateMappingProperties[];
	},
);

export const createAdpRateMapping = createAsyncThunk(
	'adpRateMapping/create',
	async (data: AdpRateMappingCreateProperties, thunkAPI) => {
		const response = await API.ClientAPI.AdpRateMapping.create(data);
		if (response.error) return thunkAPI.rejectWithValue(response.error);

		return response as AdpRateMappingProperties;
	},
);

export const updateAdpRateMapping = createAsyncThunk(
	'adpRateMapping/update',
	async ({ id, data }: { data: AdpRateMappingCreateProperties; id: string }, thunkAPI) => {
		const response = await API.ClientAPI.AdpRateMapping.update(id, data);
		if (response.error) return thunkAPI.rejectWithValue(response.error);

		return response as AdpRateMappingProperties;
	},
);

export const deleteAdpRateMapping = createAsyncThunk(
	'adpRateMapping/delete',
	async (item: { id: string; serviceCode?: boolean }, thunkAPI) => {
		const { id, serviceCode } = item;
		const response = await API.ClientAPI.AdpRateMapping.delete(id, serviceCode);
		if (response.error) return thunkAPI.rejectWithValue(response.error);

		return id;
	},
);

const adpRateMappingSlice = createSlice({
	name: 'adpRateMapping',
	initialState,
	reducers: {
		setAdpRateMappingView(state, action: PayloadAction<boolean>) {
			state.serviceCodeView = action.payload;
		},
	},
	extraReducers: (builder) => {
		// getAll
		builder.addCase(getAdpRateMappings.pending, (state, action) => {
			state.currentRequestId = action.meta.requestId;
			state.loadFailed = false;
			state.mappings = [];
			state.loading = true;
			state.error = initialState.error;
		});
		builder.addCase(getAdpRateMappings.fulfilled, (state, action) => {
			if (state.currentRequestId !== action.meta.requestId) return;
			state.loading = false;
			state.mappings = action.payload;
		});
		builder.addCase(getAdpRateMappings.rejected, (state, action) => {
			if (state.currentRequestId !== action.meta.requestId) return;
			state.loadFailed = true;
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});

		// create
		builder.addCase(createAdpRateMapping.pending, (state) => {
			state.loading = true;
			state.error = initialState.error;
		});
		builder.addCase(createAdpRateMapping.fulfilled, (state, action) => {
			state.loading = false;
			if (Boolean(action.payload.serviceCodeId) === state.serviceCodeView) state.mappings.push(action.payload);
		});
		builder.addCase(createAdpRateMapping.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});

		// update
		builder.addCase(updateAdpRateMapping.pending, (state) => {
			state.loading = true;
			state.error = initialState.error;
		});
		builder.addCase(updateAdpRateMapping.fulfilled, (state, action) => {
			state.loading = false;
			const idx = state.mappings.findIndex((m) => m.id === action.payload.id);
			if (idx >= 0) state.mappings[idx] = action.payload;
		});
		builder.addCase(updateAdpRateMapping.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});

		// delete
		builder.addCase(deleteAdpRateMapping.pending, (state) => {
			state.loading = true;
			state.error = initialState.error;
		});
		builder.addCase(deleteAdpRateMapping.fulfilled, (state, action) => {
			state.loading = false;
			state.mappings = state.mappings.filter((m) => m.id !== action.payload);
		});
		builder.addCase(deleteAdpRateMapping.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});

		// reset
		builder.addCase(resetState, () => initialState);
	},
});

export const { setAdpRateMappingView } = adpRateMappingSlice.actions;
export default adpRateMappingSlice.reducer;
